// src/components/ImprovedDownloadModal.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { TbCrown, TbDownload, TbCheck, TbX, TbDiscount } from 'react-icons/tb';
import { HiSparkles } from 'react-icons/hi';

const DownloadModal = ({ 
  isOpen, 
  closeModal, 
  selectedTemplate, 
  onDownload, 
  freeTemplates = ['Professional', 'Classic', 'Dynamic', 'Modern']
}) => {
  const navigate = useNavigate();
  
  // Check if selected template is in the free templates list
  const isTemplateAvailable = freeTemplates.includes(selectedTemplate);
  
  const handleUpgrade = () => {
    closeModal();
    navigate('/plans', { 
      state: { 
        message: "You've selected a premium template. Upgrade to download unlimited premium templates." 
      } 
    });
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                {isTemplateAvailable ? (
                  // Free Template Content
                  <>
                    <Dialog.Title as="h3" className="text-xl font-slimbold leading-6 text-gray-900 flex items-center mb-6 p-6">
                      <TbDownload className="mr-2 text-blue-500" />
                      Download Your Document
                    </Dialog.Title>
                    <div className="px-6 pb-6">
                      <p className="text-gray-600 mb-6">
                        You're downloading the <span className="font-semibold">{selectedTemplate}</span> template.
                      </p>
                      
                      <div className="mt-6 flex space-x-3 justify-between">
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
                          onClick={closeModal}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none"
                          onClick={onDownload}
                        >
                          Download Now
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  // Enhanced Premium Template Content
                  <>
                    {/* Premium Banner */}
                    <div className="bg-gradient-to-r from-violet-600 to-indigo-700 py-4 px-6 flex items-center">
                      <div className="w-12 h-12 bg-yellow-300 rounded-full flex items-center justify-center mr-4">
                        <TbCrown className="text-2xl text-indigo-800" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-white text-xl font-bold">Premium Template</h3>
                        <p className="text-indigo-100 text-sm">Upgrade to download and use this exclusive design</p>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-6">
                      <div className="mb-5 flex items-center">
                        
                        <div>
                          <p className="text-gray-700 font-medium">
                            <span className="font-bold text-indigo-700">{selectedTemplate}</span> is a premium template only available to our premium users.
                          </p>
                        </div>
                      </div>
                      
                      {/* <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-4 rounded-xl mb-5 border border-amber-100">
                        <div className="flex items-center mb-2">
                          <TbDiscount className="text-amber-600 text-xl mr-2" />
                          <p className="text-amber-800 font-bold">Limited Time Offer</p>
                        </div>
                        <p className="text-gray-700 text-sm">
                          Upgrade today and get <span className="font-bold">25% off</span> your first month
                          plus unlimited access to all premium features
                        </p>
                      </div> */}
                      
                      <div className="space-y-3 mb-6">
                        <h4 className="font-bold text-gray-800 flex items-center">
                          <span className="flex-1">Premium Benefits:</span>
                        </h4>
                        
                        {/* Benefits list with improved styling */}
                        <div className="space-y-3">
                          <div className="flex items-start">
                            <div className="mt-0.5 bg-green-100 rounded-full p-1 mr-3">
                              <TbCheck className="text-green-600" />
                            </div>
                            <div>
                              <p className="text-gray-700 font-medium">Unlimited Downloads</p>
                              {/* <p className="text-xs text-gray-500">Download as many resumes and cover letters as you need</p> */}
                            </div>
                          </div>
                          
                          <div className="flex items-start">
                            <div className="mt-0.5 bg-green-100 rounded-full p-1 mr-3">
                              <TbCheck className="text-green-600" />
                            </div>
                            <div>
                              <p className="text-gray-700 font-medium">22+ ATS-optimized Premium Templates</p>
                              {/* <p className="text-xs text-gray-500">Access our entire library of premium ATS-optimized designs</p> */}
                            </div>
                          </div>
                          
                          <div className="flex items-start">
                            <div className="mt-0.5 bg-green-100 rounded-full p-1 mr-3">
                              <TbCheck className="text-green-600" />
                            </div>
                            <div>
                              <p className="text-gray-700 font-medium">AI-Powered Optimization</p>
                              {/* <p className="text-xs text-gray-500">Get AI feedback to make your resume stand out to recruiters</p> */}
                            </div>
                          </div>
                          
                          <div className="flex items-start">
                            <div className="mt-0.5 bg-green-100 rounded-full p-1 mr-3">
                              <TbCheck className="text-green-600" />
                            </div>
                            <div>
                              <p className="text-gray-700 font-medium">Advanced Customization Options</p>
                              {/* <p className="text-xs text-gray-500">Personalize fonts, colors, sections and layouts</p> */}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Call to action buttons */}
                      <div className="flex justify-center mt-6">
                        {/* <button
                          type="button"
                          className="flex-1 py-2.5 border border-gray-300 rounded-lg text-gray-600 font-medium hover:bg-gray-50 transition-colors flex items-center justify-center"
                          onClick={closeModal}
                        >
                          <TbX className="mr-2" />
                          Maybe Later
                        </button> */}
                        <button
                          type="button"
                          className="px-10 py-2.5 bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-medium rounded-lg hover:from-indigo-700 hover:to-violet-700 transition-colors shadow-md shadow-indigo-200 flex items-center justify-center"
                          onClick={handleUpgrade}
                        >
                          <TbCrown className="mr-2" size={22}/>
                          Upgrade Now
                        </button>
                      </div>
                      
                      <p className="text-xs text-center text-gray-500 mt-4">
                     One-time payment only. 7-day money back guarantee.
                      </p>
                    </div>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DownloadModal;